// Geboortevenster · Planetenlaag: poort.lijn-sporen per planeet en kant, de losse wissels voor de markerlane, en de planetenmarge voor de indicator.
// Overzicht van de feature: EgituraPlatform/docs/human-design/hd-birth-window.md

import {
  HdBirthWindowBlockState,
  HdBirthWindowData,
  HdBirthWindowPlanetBlock,
  HdBirthWindowPlanetChange,
  HdBirthWindowPlanetLane,
  HdBirthWindowSegment,
} from './birth-window.models';

/**
 * Planetenlaag (feedbackronde 1): per planeet een lane met een personality-
 * en een designspoor van poort.lijn-blokken, plus de losse wissels voor de
 * samenvattende markerlane. Kleur/toon/base van Zon en Knopen tellen hier
 * niet (die zitten in de lagen Variabelen en Base).
 */

/** Maia planet-id-volgorde, zonder Chiron (13): dezelfde volgorde als de planeetgrid. */
export const HD_BIRTH_WINDOW_PLANET_IDS: readonly number[] = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

function sameGateLine(a: HdBirthWindowSegment, b: HdBirthWindowSegment): boolean {
  return a.gate === b.gate && a.line === b.line;
}

/** Segmenten samengevoegd op poort.lijn (kleur/toon/base-wissels verdwijnen). */
function mergeToGateLine(segments: readonly HdBirthWindowSegment[]): HdBirthWindowSegment[] {
  const merged: HdBirthWindowSegment[] = [];
  for (const segment of segments) {
    const last = merged[merged.length - 1];
    if (last && sameGateLine(last, segment) && last.toMs === segment.fromMs) {
      merged[merged.length - 1] = { fromMs: last.fromMs, toMs: segment.toMs, gate: last.gate, line: last.line };
    } else {
      merged.push({ fromMs: segment.fromMs, toMs: segment.toMs, gate: segment.gate, line: segment.line });
    }
  }
  return merged;
}

function stateOf(block: HdBirthWindowSegment, birth: HdBirthWindowSegment | undefined, containsBirth: boolean): HdBirthWindowBlockState {
  if (containsBirth) return 'birth';
  return birth && sameGateLine(block, birth) ? 'same' : 'different';
}

export function buildBirthWindowPlanetLanes(data: HdBirthWindowData, birthMs: number): HdBirthWindowPlanetLane[] {
  const lanes: HdBirthWindowPlanetLane[] = [];
  for (const planetId of HD_BIRTH_WINDOW_PLANET_IDS) {
    const bodies = data.bodies.filter(body => body.planetId === planetId);
    if (bodies.length === 0) continue;
    const tracks = (['personality', 'design'] as const)
      .map(side => bodies.find(body => body.side === side))
      .filter((body): body is NonNullable<typeof body> => body != null)
      .map(body => {
        const merged = mergeToGateLine(body.segments);
        const birthIndex = merged.findIndex((segment, index) =>
          birthMs >= segment.fromMs && (birthMs < segment.toMs || (index === merged.length - 1 && birthMs <= segment.toMs)));
        const birth = birthIndex >= 0 ? merged[birthIndex] : undefined;
        const blocks: HdBirthWindowPlanetBlock[] = merged.map((segment, index) => ({
          fromMs: segment.fromMs,
          toMs: segment.toMs,
          gate: segment.gate,
          line: segment.line,
          state: stateOf(segment, birth, index === birthIndex),
        }));
        return { side: body.side, blocks };
      });
    lanes.push({ planetId, sourceKey: bodies[0].sourceKey, tracks });
  }
  return lanes;
}

/** Alle poort-/lijnwissels in het venster, chronologisch. */
export function collectBirthWindowPlanetChanges(data: HdBirthWindowData): HdBirthWindowPlanetChange[] {
  const changes: HdBirthWindowPlanetChange[] = [];
  for (const body of data.bodies) {
    const merged = mergeToGateLine(body.segments);
    for (let i = 1; i < merged.length; i++) {
      changes.push({ ms: merged[i].fromMs, side: body.side, planetId: body.planetId, sourceKey: body.sourceKey, from: merged[i - 1], to: merged[i] });
    }
  }
  return changes.sort((a, b) => a.ms - b.ms);
}

/** Dichtstbijzijnde poort-/lijnwissel vóór en na de geboorte (ms-afstanden), voor de planetenlaag van de indicator. */
export function nearestBirthWindowPlanetChange(data: HdBirthWindowData, birthMs: number): { beforeMs?: number; afterMs?: number } {
  let beforeMs: number | undefined;
  let afterMs: number | undefined;
  for (const change of collectBirthWindowPlanetChanges(data)) {
    if (change.ms <= birthMs) {
      const distance = birthMs - change.ms;
      if (beforeMs === undefined || distance < beforeMs) beforeMs = distance;
    } else {
      const distance = change.ms - birthMs;
      if (afterMs === undefined || distance < afterMs) afterMs = distance;
    }
  }
  return { beforeMs, afterMs };
}
